import React from "react";
import moment from "moment";

// Shared Mobile Week View grid — the exact same Monday–Saturday layout (day/date headers, time
// column, horizontal scroll, responsive ~54px columns, current-day/current-hour/past styling,
// spacing) used by the Calendar screen (SchedulerTest.jsx) and now the Termini screen
// (TimeslotsSection.js) too, so both stay pixel-identical and can't drift apart. Extracted here
// specifically so Termini could reuse it instead of getting its own copy — see the Mobile
// Termini bugfix that removed its old single-day list in favor of this.
// Entirely presentational: every screen-specific behavior (what a slot shows/does, what a
// day-header tap does, if anything) is passed in — this only owns the `.hf-cal-m-week-*`
// grid/CSS structure (scheduler.css), not any booking/timeslot business logic.
const MobileWeekGrid = ({
  weekDates,
  dayLabels,
  hours,
  breakAfterHour,
  breakLabel = "12–15h",
  breakStartHour = 12,
  breakEndHour = 15,
  renderCell,
  onDayHeaderClick,
  gridClassName = "",
}) => {
  // Same "is this the current hour" concept regular slots already get (SchedulerTest.jsx's
  // `renderTimeSlot` -> `.hf-slot-current`), just for the break period as a whole instead of one
  // hour: active whenever *today* is one of the visible week's days AND the wall-clock hour is
  // inside [breakStartHour, breakEndHour) — 12:00-14:59 for the default 12/15, matching exactly
  // how the regular per-hour check is itself hour-granular, not minute-granular. Visual only —
  // nothing here makes the break selectable/bookable.
  const now = new Date();
  const isBreakActive =
    now.getHours() >= breakStartHour &&
    now.getHours() < breakEndHour &&
    weekDates.some((d) => moment(d).isSame(now, "day"));

  return (
    <div className="hf-cal-m-week-scroll">
      <div className={`hf-cal-m-week-grid${gridClassName ? ` ${gridClassName}` : ""}`}>
        <div className="hf-cal-m-week-corner" />
        {weekDates.map((date, index) => {
          const isToday = moment(date).isSame(moment(), "day");
          return (
            <button
              key={`wh-${index}`}
              type="button"
              className={`hf-cal-m-week-daycol-header${isToday ? " hf-cal-m-week-today" : ""}`}
              onClick={onDayHeaderClick ? () => onDayHeaderClick(index) : undefined}
            >
              <span className="hf-cal-m-week-daycol-label">
                {dayLabels[index].toUpperCase()}
              </span>
              <span className="hf-cal-m-week-daycol-num">{date.getDate()}</span>
              {isToday && <span className="hf-cal-m-daychip-dot" />}
            </button>
          );
        })}

        {hours.map((hour) => (
          <React.Fragment key={hour}>
            <div
              className={`hf-cal-m-week-hour${
                hour === new Date().getHours() ? " hf-cal-m-week-current-hour" : ""
              }`}
            >{`${hour}h`}</div>
            {weekDates.map((date, dayIndex) => (
              <div key={`${hour}-${dayIndex}`} className="hf-cal-m-week-cell">
                {renderCell(date, hour)}
              </div>
            ))}
            {breakAfterHour != null && hour === breakAfterHour && (
              <div
                className={`hf-cal-m-week-break${
                  isBreakActive ? " hf-cal-m-week-break-current" : ""
                }`}
                aria-hidden="true"
              >
                <span className="hf-cal-break-label hf-cal-break-label-compact">
                  {breakLabel}
                </span>
                <span className="hf-cal-break-bar" />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default MobileWeekGrid;
